/* eslint-disable no-nested-ternary */
import { IPaginationResult } from '@models/Equipment.model';
import { removeDuplicatesArray, updateBeamUrl } from '@utils';
import { sortStr } from '@utils/sorting/non-ascii';
import Manufacturer, {
  IManufacturer,
  IManufacturerApiModel,
} from './Manufacturer.model';
import Product, { IProduct, IProductApiModel } from './Product.model';
import Tag, { ITag, ITagApiModel, ITagRaw } from './Tag.model';
import { UpdateStatusPriority, UpdateStatusType } from './UpdateStatus.model';

export const updateTypeOptions = <const>[
  'critical',
  'high',
  'low',
  'scheduled',
];
export type UpdateTypeOptions = typeof updateTypeOptions[number];

export const getTypeColor = (importance: UpdateTypeOptions) => {
  if (importance === 'critical') return '#EA1212';
  if (importance === 'high') return '#DF6F00';
  if (importance === 'low') return '#838A91';
  if (importance === 'scheduled') return '#249D0E';

  return '#249D0E';
};

export enum UpdateType {
  PRODUCTS = 'products',
  MANUFACTURERS = 'manufacturers',
  TAGS = 'tags',
}

export interface IUpdate {
  id: string;
  subject: string;
  description: string;
  originalMessage: string;
  studioId: string;
  manufacturers: IManufacturer[];
  products: IProduct[];
  tags: ITag[];
  dataDate?: Date;
  type: UpdateType;
  createdAt: Date;
  updatedAt: Date;
}

export interface IUpdateApiModel {
  id: string;
  subject: string;
  description: string;
  originalMessage: string;
  studioId: string;
  manufacturers: IManufacturerApiModel[];
  products: IProductApiModel[];
  tags: ITagApiModel[];
  dataDate?: string;
  type: UpdateType;
  createdAt: string;
  updatedAt: string;
}

interface ITagAgg {
  f1: string;
  f2: string;
  f3: string;
  f4: string;
  f5: string;
}

export interface IUpdateRawQueryList {
  id: string;
  subject: string;
  type: string;
  priority: UpdateStatusPriority | null;
  assignee: string;
  date?: Date;
  dueDate?: Date;
  usType: UpdateStatusType;
  mName: string;
  usStudioId: string;
  usId: string;
  mImage: string;
  affects: number;
  description: string;
  message: string;
  aName?: string;
  aAvatar?: string;
  tags: Array<ITagRaw>;
  pManufacturers: Array<string>;
  pManufacturer?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface IUpdateRawQueryListApiModel {
  id: string;
  subject: string;
  type: string;
  priority: UpdateStatusPriority;
  assignee: string;
  date: string;
  dueDate: string;
  usType: UpdateStatusType;
  mName: string;
  mImage: string;
  usStudioId: string;
  usId: string;
  affects: string;
  description: string;
  message: string;
  aName?: string;
  aAvatar?: string;
  tags: Array<ITagAgg>;
  pManufacturers: Array<Array<string>>;
  createdAt: string;
  updatedAt: string;
}

export default class Update implements IUpdate {
  public readonly id: string;

  public readonly subject: string;

  public readonly description: string;

  public readonly originalMessage: string;

  public readonly studioId: string;

  public readonly manufacturers: IManufacturer[];

  public readonly products: IProduct[];

  public readonly tags: ITag[];

  public readonly dataDate?: Date;

  public readonly dueDate?: Date;

  public readonly type: UpdateType;

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  constructor(
    id: string,
    subject: string,
    description: string,
    originalMessage: string,
    studioId: string,
    manufacturers: IManufacturer[],
    products: IProduct[],
    tags: ITag[],
    createdAt: Date,
    updatedAt: Date,
    type: UpdateType,
    dataDate?: Date,
    dueDate?: Date,
  ) {
    this.id = id;
    this.subject = subject;
    this.description = description;
    this.originalMessage = originalMessage;
    this.studioId = studioId;
    this.manufacturers = manufacturers;
    this.products = products;
    this.tags = tags;
    this.dataDate = dataDate;
    this.dueDate = dueDate;
    this.type = type;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
  }

  static fromApiModel(apiModel: IUpdateApiModel) {
    const { id, subject, description, originalMessage, studioId, type } =
      apiModel;

    const dataDate = apiModel.dataDate
      ? new Date(Date.parse(apiModel.dataDate))
      : undefined;
    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(apiModel.updatedAt);

    const products = Product.arrayFromApiModel(apiModel.products);
    const manufacturers = Manufacturer.arrayFromApiModel(
      apiModel.manufacturers,
    );
    const apiModelTags = apiModel.tags || [];
    const tags = Tag.arrayFromApiModel(apiModelTags);

    return new Update(
      id,
      subject,
      description,
      originalMessage,
      studioId,
      manufacturers,
      products,
      tags,
      createdAt,
      updatedAt,
      type,
      dataDate,
    );
  }

  static arrayFromApiModel(arrayApiModel: IUpdateApiModel[]) {
    const array = arrayApiModel.map(apiModel => Update.fromApiModel(apiModel));

    return array;
  }

  static arrayPaginatedFromApiModel(
    arrayApiModel: IPaginationResult<IUpdateApiModel>,
  ) {
    const array = arrayApiModel.results.map(apiModel =>
      Update.fromApiModel(apiModel),
    );

    return {
      ...arrayApiModel,
      results: array,
    };
  }

  static arrayPaginatedFromApiAdmModel(
    arrayApiModel: IPaginationResult<IUpdateApiModel>,
  ) {
    const array = arrayApiModel.results.map(apiModel =>
      Update.fromApiModel(apiModel),
    );

    return {
      ...arrayApiModel,
      results: array,
    };
  }

  static fromRawQueryListApiModel(
    apiModel: IUpdateRawQueryListApiModel,
  ): IUpdateRawQueryList {
    const {
      id,
      assignee,
      mImage,
      mName,
      subject,
      type,
      priority,
      usType,
      usId,
      usStudioId,
      description,
      message,
      aName,
    } = apiModel;

    const aAvatar = apiModel.aAvatar
      ? updateBeamUrl(apiModel.aAvatar)
      : undefined;
    const apiModelTags = apiModel.tags || [];
    const tagsFlat = apiModelTags.flat(1);
    const tagsFlatWithoutNull = tagsFlat.filter(item => !!item && !!item.f1);
    const tagsMap: ITagRaw[] = tagsFlatWithoutNull.map(
      ({ f1, f2, f3, f4, f5 }) => ({
        id: f1,
        name: f2,
        color: f3,
        tcId: f4,
        tcName: f5,
      }),
    );
    const _tags = removeDuplicatesArray(tagsMap);
    const tags = Array.from(_tags).sort((a, b) => {
      return sortStr(a.name, b.name);
    });
    const pManufacturersDuplicated = apiModel.pManufacturers?.flat(1) || [];
    const pManufacturersWithNull = pManufacturersDuplicated.filter(
      (item, pos) => pManufacturersDuplicated.indexOf(item) === pos,
    );
    const pManufacturers = pManufacturersWithNull.filter(item => item !== null);

    const pManufacturer = pManufacturersDuplicated[0];

    const affects = Number(apiModel.affects);
    const date = apiModel.date
      ? new Date(Date.parse(apiModel.date))
      : undefined;
    const dueDate = apiModel.dueDate
      ? new Date(Date.parse(apiModel.dueDate))
      : undefined;
    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));

    return {
      id,
      assignee,
      mImage,
      mName,
      subject,
      usId,
      usStudioId,
      type,
      priority,
      usType,
      date,
      dueDate,
      tags,
      affects,
      description,
      message,
      pManufacturers,
      pManufacturer,
      aAvatar,
      aName,
      createdAt,
      updatedAt,
    };
  }

  static arrayFromRawQueryListApiModel(
    arrayApiModel: IUpdateRawQueryListApiModel[],
  ): IUpdateRawQueryList[] {
    const array = arrayApiModel.map(apiModel =>
      Update.fromRawQueryListApiModel(apiModel),
    );

    return array;
  }

  static arrayPaginatedFromRawQueryListApiModel(
    arrayApiModel: IPaginationResult<IUpdateRawQueryListApiModel>,
  ): IPaginationResult<IUpdateRawQueryList> {
    const array = arrayApiModel.results.map(apiModel =>
      Update.fromRawQueryListApiModel(apiModel),
    );

    return {
      ...arrayApiModel,
      results: array,
    };
  }
}
